import { getUserFromCookie } from 'util/auth';
import {
  getGtmVarsForPageLoadedEvent,
  getGtmVarsForBundlesLoadedEvent,
  PAGE_LOADED_TRIGGER,
  BUNDLES_LOADED_TRIGGER,
  DEFAULT_GTM_DATA
} from 'util/gtm';
import { SITE_KEY } from 'constants/site';

export const DATALAYER_NAME = 'dataLayer';

const dlPush = data => {
  if (!(DATALAYER_NAME in window)) {
    return;
  }

  window[DATALAYER_NAME].push(data);
};

/**
 * modified version of google's init script. just handles the dataLayer creation
 * and initial pushing of config. not using script tag creation because it's
 * handled via script/async tag in component.
 *
 * https://developers.google.com/tag-manager/devguide#datalayer
 *
 * @export
 */
export function initGtm() {
  // eslint-disable-next-line no-param-reassign
  window[DATALAYER_NAME] = window[DATALAYER_NAME] || [];

  dlPush({
    // inform our container which site is being rendered
    siteKey: SITE_KEY,

    // timing metadata
    'gtm.start': new Date().getTime(),

    // sends the default GTM "Page View" event signaling the container has
    // loaded. note that this does NOT cause GTM to fire our GA pageview event;
    // that is called by pageLoaded below.
    event: 'gtm.js'
  });
}

/**
 * fires our custom page view trigger and passes basic and custom dimensions as
 * custom GTM vars so we can utilize them in GA pageview and other tags.
 *
 * @export
 * @param {String} path path including query string
 * @param {Object} metadata from extractMetadataFromBundleItems()
 * @param {String} pageType ends up in contentGroup1 (Home Page, Post Page, etc.)
 * @param {String} httpStatus descriptive HTTP status message (CODE: msg)
 * @param {String[]} flips enabled feature flips
 * @param {String} ssrRenderDatetime datetime page was rendered in ISO 8601 format
 * @param {String} deployId version of the app running in prod
 */
export function pageLoaded(
  path,
  metadata,
  pageType,
  httpStatus,
  flips,
  ssrRenderDatetime,
  deployId,
  surrogateKey
) {
  const user = getUserFromCookie();

  const currentPageGtmVars = getGtmVarsForPageLoadedEvent({
    path,
    metadata,
    pageType,
    httpStatus,
    user,
    flips,
    ssrRenderDatetime,
    deployId,
    surrogateKey
  });

  dlPush({
    event: PAGE_LOADED_TRIGGER,
    // merging with default data first ensures GTM doesn't retain any data from
    // previous pages.
    ...DEFAULT_GTM_DATA,
    ...currentPageGtmVars
  });
}

/**
 * fires a custom trigger for signaling more bundles have been loaded/displayed
 * in the page.
 *
 * NOTE: we don't need to reset/prime all GTM vars since this is an event that
 * happens after a page has been loaded.
 *
 * @export
 * @param {Number} bundleCount number of bundles now present in page
 */
export function bundlesLoaded(bundleCount) {
  const otherGtmVars = getGtmVarsForBundlesLoadedEvent({ bundleCount });
  dlPush({
    event: BUNDLES_LOADED_TRIGGER,
    ...otherGtmVars
  });
}

export function customEvent(eventName, data) {
  dlPush({
    event: eventName,
    ...data
  });
}
